/**
 * Ряды «Голоса клиента» по корзинам — `GET /api/v1/analytics/voice/series`
 * (#2394 §9 п. 5, docs/plans/2026-10-18-voc-trends-read.md §3; спека #847 §3).
 *
 * Ряд — один сигнал фактов (`vocFactSignalSchema`), до
 * `VOICE_TREND_MAX_KEYS` строк (ключ — как ключ строки раздела: код, уровень,
 * тип или сведённое имя конкурента), корзины — день, неделя (с дня
 * организации) или месяц в поясе смотрящего. Окно — не длиннее горизонта
 * `VOICE_TREND_MAX_DAYS`: старше срок хранения данных не держит (v1).
 *
 * Непокрытая корзина — `null`, не ноль: данных за неё нет (до первой записи
 * области читателя) или факты за неё не заполнены.
 */
import { z } from 'zod/v4';
import { lockedDemoOnlySchema, projectIdsFilterSchema } from './common';
import { voiceThemeFilterCodeSchema } from './voice-of-customer';
import { callCategorySchema } from './records';
import { VOC_SIGNAL_KEY_MAX_LENGTH, vocFactSignalSchema } from './voc-facts';

/** Горизонт ряда в v1 — продуктовый потолок срока хранения (спека #847 §1, §3). */
export const VOICE_TREND_MAX_DAYS = 365;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Окно длиннее потолка ряда: на один день больше номинального года — полный
 * високосный год (2028-01-01..2029-01-01) это 366 дней, и календарный год —
 * то, что просит смотрящий. Один счёт для ряда и для маркеров
 * (`include=voice` у `/analytics/config-events`).
 */
export function voiceTrendWindowTooLong(startMs: number, endMs: number): boolean {
  return endMs - startMs > (VOICE_TREND_MAX_DAYS + 1) * DAY_MS;
}

/** Сколько строк в одном ряду; без `keys` — столько самых частых. */
export const VOICE_TREND_MAX_KEYS = 20;

export const VOICE_TREND_BUCKETS = ['day', 'week', 'month'] as const;

// Code points, as the writer cuts the key (`VOC_SIGNAL_KEY_MAX_LENGTH`): a key
// of 101 emoji is 202 UTF-16 units and still a key the series returns.
const trendKeySchema = z
  .string()
  .min(1)
  .max(VOC_SIGNAL_KEY_MAX_LENGTH * 2)
  .refine((key) => Array.from(key).length <= VOC_SIGNAL_KEY_MAX_LENGTH, {
    message: `Key is longer than ${VOC_SIGNAL_KEY_MAX_LENGTH} characters`,
  });

export const voiceTrendQuerySchema = z
  .object({
    signal: vocFactSignalSchema,
    /** Ключи строк (повтором параметра); нет — самые частые за окно. */
    keys: z
      .union([trendKeySchema, z.array(trendKeySchema)])
      .optional()
      .transform((keys) =>
        keys === undefined ? undefined : [...new Set(Array.isArray(keys) ? keys : [keys])]
      )
      .refine((keys) => keys === undefined || keys.length <= VOICE_TREND_MAX_KEYS, {
        message: `Too many keys (max ${VOICE_TREND_MAX_KEYS})`,
      }),
    bucket: z.enum(VOICE_TREND_BUCKETS),
    /** Первый день недели организации (ISO: 1 — понедельник); нет — понедельник. */
    weekStartsOn: z.coerce.number().int().min(1).max(7).optional(),
    /** IANA-пояс смотрящего: по нему режутся дни, недели и месяцы. */
    timeZone: z.string().min(1).max(64),
    periodStart: z.string().datetime({ offset: true }),
    periodEnd: z.string().datetime({ offset: true }),
    /** @deprecated legacy single-project filter — see callStatsQuerySchema's note. */
    projectId: z.string().optional(),
    projectIds: projectIdsFilterSchema,
    callCategory: callCategorySchema.optional(),
    includeDemo: z.enum(['yes', 'no']).optional(),
    /** Фильтр темы — как у раздела (#2098): ряд только по разговорам темы. */
    themeCode: voiceThemeFilterCodeSchema.optional(),
  })
  .refine((query) => Date.parse(query.periodStart) < Date.parse(query.periodEnd), {
    message: 'periodStart must be before periodEnd',
    path: ['periodStart'],
  });

export const voiceTrendBucketSchema = z.object({
  /** Первый местный день корзины `YYYY-MM-DD`. */
  label: z.string(),
  start: z.string(),
  /** Конец корзины, не включая. */
  end: z.string(),
  /** N блока сигнала; `null` — корзина не покрыта. */
  calls: z.number().int().nonnegative().nullable(),
  covered: z.boolean(),
});

export const voiceTrendRowSchema = z.object({
  key: z.string(),
  /** K по корзинам; `null` — корзина не покрыта (у темы — и до её базы). */
  counts: z.array(z.number().int().nonnegative().nullable()),
});

export const voiceTrendResponseSchema = z.object({
  periodStart: z.string(),
  demoOnly: lockedDemoOnlySchema,
  periodEnd: z.string(),
  signal: vocFactSignalSchema,
  bucket: z.enum(VOICE_TREND_BUCKETS),
  buckets: z.array(voiceTrendBucketSchema),
  rows: z.array(voiceTrendRowSchema),
  /** Номера корзин, с которых сменилась версия набора кодов: ряд рисуется с разрывом. */
  breaks: z.array(z.number().int().nonnegative()),
  /**
   * Номера корзин, с которых сменилось чтение темы — ревизия промпта тем или
   * модель шага (#3144): у ряда темы и у ряда под фильтром темы. Ряд рисуется с
   * разрывом и здесь; причина своя, поэтому отдельно от `breaks`. Нет поля —
   * таких корзин нет.
   */
  themeBreaks: z.array(z.number().int().nonnegative()).optional(),
  /**
   * Часть ключей окна (любых, не только строк ответа) сведена по другой
   * версии (глоссарий конкурентов, карта меток возражений): строки могут
   * дробиться, а топ — смениться после пересведения.
   */
  resolutionStale: z.boolean(),
});

export type VoiceTrendQuery = z.infer<typeof voiceTrendQuerySchema>;
export type VoiceTrendBucket = z.infer<typeof voiceTrendBucketSchema>;
export type VoiceTrendRow = z.infer<typeof voiceTrendRowSchema>;
export type VoiceTrendResponse = z.infer<typeof voiceTrendResponseSchema>;
